import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { Zoom, Fade, Typography, Tooltip, TextareaAutosize, TextField, InputAdornment, Menu, Divider } from '@mui/material';
import { PriorityHigh, ArrowDownward, Delete, MoreHoriz, DragHandle } from '@mui/icons-material'
import moment from 'moment';

import "./PresentationCard.css";
import ProgressIndicator from '../ProgressIndicator/ProgressIndicator';
import { MIN_PRESENTER_TIME, adjustPresentersTime, getExpectedMeetingLength } from '../../TimeHelper';

/** ICON CONSTANTS */
const PRIORITY_STRINGS = ['Low Priority', 'Standard Priority', 'High Priority']
const PRIORITY_ICON_COLOURS = ['#848587', '#818182', '#656566']
const DELETE_ICON_COLOR = '#848587';

/** CARD BACKGROUND COLOUR CONSTANTS */
const CARD_INITIAL_COLOR = 'rgb(230,230,230)';
const CARD_NAME_INITIAL_COLOR = 'rgb(240,240,240)';
const CARD_COMPLETED_COLOR = 'rgb(240,240,240)';
const CARD_ACTIVE_COLOR = 'rgb(198,246,241)';
const GRADIENT_BLENDING_DISTANCE = 15
const CARD_PROGRESS_ON_TIME_COLOR = '#c6f6f1'
const CARD_PROGRESS_OVERTIME_COLOR = '#c7b6f7'
const CARD_PROGRESS_GRADIENT = ['#c6f6f1', '#c7eef2', '#c7e6f3', '#c8def4', '#c8d6f4', '#c8cef5', '#c8c6f6', '#c8bef6', '#c7b6f7']

/** Left-hand start/end labels always use minute precision so the gutter stays narrow. */
const SCHEDULE_LABEL_TIME_FORMAT = 'h:mm a';
/** Scheduled vs projected end must differ by more than this before showing the red overtime line. */
const END_TIME_TOLERANCE_SECONDS = 60;

const formatScheduleLabel = (time: moment.MomentInput): string =>
  moment(time).format(SCHEDULE_LABEL_TIME_FORMAT);

interface PresentationCardInterface {
  cookies: any; /** user identifying cookie */
  cursor: number; /** The index of the current presenter (in relation to presenters) */
  displaySeconds: boolean; /** Whether or not seconds should be displayed for presentations */
  isDebugModeEnabled: boolean; /** Debug runs 5× faster — slot timers always need second precision */
  dragging: boolean; /** Is any presentation actively being moved */
  dynamicFontSize: number;  /** The calculated font size for the presentation cards */
  heartBeatTrigger: boolean; /** A boolean that is toggled on a heart beat */
  index: number; /** Index of the presentation (in presenters) */
  isActive: boolean;  /** Is the presentation currently happening */
  isAdmin: boolean; /** Whether or not app is in admin view */
  isArchived: boolean;   /** Is this presentation part of a meeting that has been archived */
  isCompleted: boolean; /** Has the presentation finished */
  isViewingCompletedSession: boolean; /** Saved / historical session (agenda edits allowed) */
  isNarrowScreen: boolean; /** Whether the screen is narrow (i.e. change the ui elements to be more condensed ) */
  isShortScreen: boolean; /** Whether the screen is short (i.e. change the ui elements to be more condensed ) */
  meetingEndTime: Date; /** The end time of the meeting, as Date object */
  meetingLength: number;  /** The scheduled meeting length */
  meetingStartTime: Date; /** The start time of the meeting, as Date object */
  presentationCardHeight: number; /** Height of the slot*/
  presenter: Presenter | Presenter;/** Presentation represented by this card */
  presenters: Presenter[]; /** A list of all the presenters */
  writeMeetingChange: (valuesToUpdate: StateUpdateObject) => void; /** Function that writes changes to the db (and locally) */
  meetingEditsEnabled: boolean;
}

export const PresentationCard = (props: PresentationCardInterface): React.ReactElement => {
  const { cookies, cursor, displaySeconds, isDebugModeEnabled, dragging, dynamicFontSize, heartBeatTrigger, index,
    isActive, isAdmin, isArchived, isCompleted, isViewingCompletedSession, isNarrowScreen, isShortScreen, meetingEndTime, meetingLength, meetingStartTime,
    presentationCardHeight, presenter, presenters, writeMeetingChange, meetingEditsEnabled,
  } = props;

  const [slotHover, setSlotHover] = useState(-2)

  const nameRef = useRef<any>()
  const [nameValue, setNameValue] = useState(presenter.name)
  const [nameBackup, setNameBackup] = useState("")
  const [nameEditFocus, setNameEditFocus] = useState(false)
  const [nameEdited, setNameEdited] = useState(false)

  const descriptionRef = useRef<any>()
  const [descriptionValue, setDescriptionValue] = useState(presenter.description)
  const [descriptionBackup, setDescriptionBackup] = useState("")
  const [descriptionFocus, setDescriptionFocus] = useState(false)
  const [descriptionEdited, setDescriptionEdited] = useState(false)
  const [descriptionOverflow, setDescriptionOverflow] = useState("")
  
  const [initialDurationValue, setInitialDurationValue] = useState(Math.floor(presenter.initialDuration/60).toString());
  const [estimatedCurrentDuration, setEstimatedCurrentDuration] = useState("");

  const timeRef = useRef<any>();
  const [timeHover, setTimeHover] = useState(false)
  
  const [priorityMenu, setPriorityMenu] = useState(false)
  const [priorityValue, setPriorityValue] = useState(presenter.priority)

  const [anchorTimeChange, setAnchorTimeChange] = React.useState<null | HTMLElement>(null);
  const openTimeChange = Boolean(anchorTimeChange);
  const handleCurrentTimeClick = () => {
    if (!isPresenterChangeable()) return;
    setInitialDurationValue(Math.floor(presenter.initialDuration/60).toString());
    setAnchorTimeChange(timeRef.current);
  }
  const handleTimeChangeClose = () => {
    setTimeHover(false);
    setAnchorTimeChange(null);
    if (initialDurationValue !== "") {
      writeMeetingChange({ presenterUpdate: [{ ...presenter, initialDuration: parseInt(initialDurationValue)*60 }] })
    }
  }
  


  // Permission Checks ===================================================================

  /** User can make a change if they are the admin or if they created the slot */
  const doesUserHavePermission = () => { return (isAdmin || (cookies && cookies.user && cookies.user.includes(presenter.id))) }

  /** Once a presenter has begun (or finished) some changes can't occur */
  const isChangeTooLate = () => {
    if (isViewingCompletedSession) {
      return false;
    }
    return isCompleted || isActive || isArchived;
  };

  /** Some presenters can not be changed, like bonus time which is automatically calculated */
  const isPresenterSlotChangable = () => { return (index !== presenters.length || presenter.name !== 'Bonus Time'); }

  /** Check the various conditions required for changing a presenter */
  const isPresenterChangeable = (): boolean => {
    return meetingEditsEnabled && doesUserHavePermission() && !isChangeTooLate() && isPresenterSlotChangable();
  }

  const hasMeetingStarted = (): boolean => { return cursor > -1 }

  const isHoveringSlot = (): boolean => { return index === slotHover }

  const isDragging = (): boolean => { return dragging }

  /** Live meetings grey out finished slots; historical replay keeps timing visuals. */
  const retainCompletedSlotTimingVisuals = (): boolean => isViewingCompletedSession;

  const showSlotDurationSeconds = (): boolean =>
    isViewingCompletedSession || displaySeconds || isDebugModeEnabled;

  const formatSlotDuration = (timeInSeconds: number, unit?: boolean): string => {
    if (showSlotDurationSeconds()) {
      const min = Math.floor(timeInSeconds / 60);
      const sec = Math.floor(timeInSeconds % 60).toString().padStart(2, '0');
      return `${min}:${sec}${unit ? ' min' : ''}`;
    }
    return `${Math.round(timeInSeconds / 60)}${unit ? ' min' : ''}`;
  };

  const slotDurationLabels = useMemo(
    () => ({
      current: formatSlotDuration(presenter.currentDuration),
      initial: formatSlotDuration(presenter.initialDuration),
    }),
    [
      presenter.currentDuration,
      presenter.initialDuration,
      displaySeconds,
      isDebugModeEnabled,
      isViewingCompletedSession,
    ]
  );

  const shouldShowDragableIcon = (): boolean => { return isPresenterChangeable() && isHoveringSlot() };

  const shouldShowPrenterTimes = () => {
    return (
      isHoveringSlot() &&
      hasMeetingStarted() &&
      presentationCardHeight > dynamicFontSize * 2 &&
      (!isArchived || isViewingCompletedSession) &&
      presenter.slotType !== 'Header'
    );
  };

  const shouldShowMeetingStartTime = () => { return (index === 0 && hasMeetingStarted() && !isDragging()) }

  const shouldShowMeetingEndTime = () => {
    return (
      hasMeetingStarted() &&
      !isDragging() &&
      (index === presenters.length - 1 || index === presenters.length) &&
      formatScheduleLabel(moment().add(getCulumativeTimeLeft(presenters, cursor), 's')) !==
        formatScheduleLabel(moment())
    );
  };

  const getScheduledEndMoment = (): moment.Moment =>
    meetingEndTime ? moment(meetingEndTime) : moment(meetingStartTime).add(meetingLength, 's');

  const areScheduledAndExpectedEndWithinTolerance = (): boolean => {
    if (!hasMeetingStarted() || !meetingStartTime) {
      return false;
    }
    const scheduled = getScheduledEndMoment();
    const expected = moment(meetingStartTime).add(getExpectedMeetingLength(presenters, cursor), 's');
    return Math.abs(scheduled.diff(expected, 'seconds')) <= END_TIME_TOLERANCE_SECONDS;
  };

  const shouldShowOverTime = () => {
    if (areScheduledAndExpectedEndWithinTolerance()) {
      return false;
    }
    let isScheduledEndTimeDuringPresentation = getTotalMeetingTimeBeforePresenter() < meetingLength && getTotalMeetingTimeBeforePresenter() + Math.max(presenter.currentDuration, presenter.elapsedTime) > meetingLength;
    return hasMeetingStarted() && !isDragging() && isScheduledEndTimeDuringPresentation
  }
  const canEditCardDescription = () => doesUserHavePermission() && isPresenterChangeable();

  const canEditCardText = () => doesUserHavePermission() && isPresenterChangeable();


  // =====================================================================================

  // Use Effects =========================================================================

  /** If the presenter's name changes, update the card */
  useEffect(() => {
    setNameValue(presenter.name);
  }, [presenter.name])

  /** If the presenter's description changes, update the card */
  useEffect(() => {
    setDescriptionValue(presenter.description);
  }, [presenter.description])

  /** If the presenter's time changes, update the card */
  useEffect(() => {
    setPriorityValue(presenter.priority)
  }, [presenter.priority])

  /** Save the current name, incase user cancels name update */
  useEffect(() => {
    setNameBackup(nameValue)
  }, [nameEditFocus === true])

  /** Save the current description, incase user cancels description update */
  useEffect(() => {
    setDescriptionBackup(descriptionValue)
  }, [descriptionFocus === true])


  useEffect(() => {
    if (presenter.slotType !== "Break" && presenter.slotType !== "Header" && descriptionRef && descriptionRef.current) {

      if (descriptionRef.current.scrollHeight > descriptionRef.current.clientHeight) {
        let numberOfLines = Math.floor(descriptionRef.current.scrollHeight / dynamicFontSize)
        if (descriptionRef.current.scrollTop === 0 && numberOfLines > 2) {
          setDescriptionOverflow("bottomGradient")
        } else if (descriptionRef.current.scrollTop >= descriptionRef.current.scrollHeight - descriptionRef.current.offsetHeight && numberOfLines > 2) {

          setDescriptionOverflow("topGradient")
        } else if (numberOfLines > 2) {
          setDescriptionOverflow("bothGradient")
        }
      } else {
        setDescriptionOverflow("")
      }
    }
  }, [descriptionValue, descriptionRef?.current?.scrollTop, heartBeatTrigger])
  // =====================================================================================

  // Handle Changes ======================================================================

  const handleDescriptionChange = () => {
    if (descriptionEdited) {
      writeMeetingChange({ presenterUpdate: [{ ...presenter, description: descriptionValue.trim() }] })
      setDescriptionEdited(false)
    }
  }
  const handleNameChange = () => {
    if (nameEdited) {
      writeMeetingChange({ presenterUpdate: [{ ...presenter, name: nameValue.trim() }] })
      setNameEdited(false)
    }
  }

  const handlePriorityChange = (value: number) => {
    if (presenter.priority !== value) {
      writeMeetingChange({ presenterUpdate: [{...presenter, priority: value}] });
      setPriorityValue(value);
    }
    setPriorityMenu(false);
  }

  //KENDRA can these be combined/simplified?
  const handleDescriptionKeydown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setDescriptionValue(descriptionBackup);
      setDescriptionEdited(false);
    }
    if (event.key === 'Escape' || event.key === 'Enter') {
      setDescriptionFocus(false);
      descriptionRef.current.blur()
    }
  }

  const handleNameKeydown = (event:  React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setNameValue(nameBackup);
      setNameEdited(false);
    }
    if (event.key === 'Escape' || event.key === 'Enter') {
      setNameEditFocus(false);
      nameRef.current.blur();
    }
  }


  const openPriorityMenu = () => { setPriorityMenu(isPresenterChangeable()) }
  // =====================================================================================

  // Helper Functions ====================================================================

  // Instead of using a for loop with a bunch of if elses/switch to check what color to give the slot, we used cleaner approach that uses division by 10 which will point to the color postion in the array of colors
  const getNameAreaColor = (): string | undefined => {
    if (!isCompleted && !isActive) return CARD_NAME_INITIAL_COLOR;
    if (isActive) return 'transparent';
    const diff = presenter.elapsedTime - presenter.currentDuration;
    if (presenter.elapsedTime <= presenter.currentDuration) {
      return CARD_ACTIVE_COLOR;
    } else {
      let paletteIndex = Math.floor(diff / presenter.currentDuration * 10)
      paletteIndex = Math.max(1, paletteIndex)
      paletteIndex = Math.min(CARD_PROGRESS_GRADIENT.length - 1, paletteIndex)
      return CARD_PROGRESS_GRADIENT[paletteIndex]
    }
  }

  const getDisplayTime = (time: number, unit?: boolean): string => formatSlotDuration(time, unit);

  /** Returns the background colour of the card (depending on scheduled time and used time) */
  const getBackgroundGradient = () => {
    let elapsedTimePercentage = (presenter.elapsedTime / presenter.currentDuration) * 100;

    if (elapsedTimePercentage <= 100) {
      /** Presenter is on time (or undertime) return undertime blue/grey gradient */
      return `linear-gradient(to right, 
        ${CARD_PROGRESS_ON_TIME_COLOR} ${elapsedTimePercentage - GRADIENT_BLENDING_DISTANCE}%,
        ${CARD_COMPLETED_COLOR} ${elapsedTimePercentage + (GRADIENT_BLENDING_DISTANCE / 2)}%)`;
    } else {
      /** Presenter is overtime return overtime purple/blue gradient */
      return `linear-gradient(to left, 
        ${CARD_PROGRESS_OVERTIME_COLOR} ${(elapsedTimePercentage - 100) - GRADIENT_BLENDING_DISTANCE}%, 
        ${CARD_PROGRESS_ON_TIME_COLOR} ${(elapsedTimePercentage - 100) + (GRADIENT_BLENDING_DISTANCE / 2)}%)`
    }
  }

  const getPresenterCardColor = () => {
    if (isCompleted && !retainCompletedSlotTimingVisuals()) return CARD_COMPLETED_COLOR;
    if (!isActive && !isCompleted) return CARD_INITIAL_COLOR;
    return getBackgroundGradient();
  }

  const getSpecialCardColor = () => {
    if (!isActive && !isCompleted) return CARD_COMPLETED_COLOR;
    if (isCompleted && !retainCompletedSlotTimingVisuals()) return CARD_INITIAL_COLOR;
    return getBackgroundGradient();
  }

  /** Returns percentage of time used (upto max 100%) */
  const getProgressIndicatorPosition = () => {
    return Math.min((presenter.elapsedTime / presenter.currentDuration), 1) * 100;
  }

  const getTotalMeetingTimeBeforePresenter = (): number => {
    let totalDurationBeforeIndex = 0
    for (let i = 0; i < Math.min(index, presenters.length); i++) {
      if (i < cursor) {
        totalDurationBeforeIndex += presenters[i].elapsedTime
      } else {
        totalDurationBeforeIndex += Math.max(presenters[i].currentDuration, presenters[i].elapsedTime)
      }
    }
    return totalDurationBeforeIndex;
  }

  const getEndIndicatorHeight = () => {
    let currentElapsed = 0
    for (let x = 0; x < presenters.length; x++) {
      if (x < cursor) {
        currentElapsed += presenters[x].elapsedTime
      } else if (x === cursor) {
        currentElapsed += Math.max(presenters[x].elapsedTime, presenters[x].currentDuration)
      } else {
        currentElapsed += presenters[x].currentDuration
      }
      if (currentElapsed >= meetingLength && Math.max(presenters[x].elapsedTime, presenters[x].currentDuration) > 0) {
        return `${(currentElapsed - meetingLength) / Math.max(presenters[x].elapsedTime, presenters[x].currentDuration) * 100}%`;
      }
    }
    return '0%';
  }

  /** Returns the start time of the presenter card indicated by index as a formatted string */
  const getPresentationStartTime = (): string => {
    /** If invalid index or presenter list, return empty string */
    if (!presenters || index > presenters.length || index < 0) return "";

    let totalDurationBeforeIndex = getTotalMeetingTimeBeforePresenter()
    return formatScheduleLabel(moment(meetingStartTime).add(totalDurationBeforeIndex, 's'));
  }

  /** Returns the end time of the presenter card indicated by index as a formatted string */
  const getPresentationEndTime = (): string => {
    /** If invalid index or presenter list, return empty string */
    if (!presenters || index < 0) return "";

    let totalDurationBeforeIndex = getTotalMeetingTimeBeforePresenter();
    totalDurationBeforeIndex += Math.max(presenter.currentDuration, presenter.elapsedTime);
    return formatScheduleLabel(moment(meetingStartTime).add(totalDurationBeforeIndex, 's'));
  }

  /** Agenda still exceeds the scheduled cap after slot adjustment. */
  const isAgendaRunningOverSchedule = (): boolean =>
    hasMeetingStarted() &&
    getExpectedMeetingLength(presenters, cursor) > meetingLength + END_TIME_TOLERANCE_SECONDS;

  const getScheduledEndTimeLabel = (): string => {
    if (meetingEndTime) {
      return formatScheduleLabel(meetingEndTime);
    }
    if (meetingStartTime) {
      return formatScheduleLabel(moment(meetingStartTime).add(meetingLength, 's'));
    }
    return getPresentationEndTime();
  };

  /** Bottom-of-agenda label: menu end when on schedule, projected end when running over. */
  const getBottomEndTimeLabel = (): string => {
    if (isAgendaRunningOverSchedule()) {
      return getPresentationEndTime();
    }
    return getScheduledEndTimeLabel();
  };


  const getCulumativeTimeLeft = (newPresenters: Presenter[], cursor: number) => {
    let newCumulativeTimeLeft = 0
    for (let x = cursor; x < newPresenters.length; x++) {
      if (newPresenters[x].elapsedTime > 0 && (newPresenters[x].elapsedTime < newPresenters[x].currentDuration)) {
        newCumulativeTimeLeft += (newPresenters[x].currentDuration - newPresenters[x].elapsedTime)
      } else if (newPresenters[x].elapsedTime < newPresenters[x].currentDuration) {
        newCumulativeTimeLeft += newPresenters[x].currentDuration
      }
    }
    return newCumulativeTimeLeft
  }


  const getDescriptionShadowSize = () => {
    return Math.floor(presentationCardHeight * 0.4);
  }
  // =====================================================================================

  // HTML Functions ======================================================================

  const createCard = (): React.ReactElement => {
    if (presenter.slotType === "Header") return createHeaderCard();
    if (presenter.slotType === "Break" || isNarrowScreen) return createBreakCard();
    
    return createPresenterCard();
  }

  const createPresenterCard = (): React.ReactElement => {
    return (
      <div className="inner-card" style={{ background: getPresenterCardColor() }}>
        {createNameArea()}
        {createDescriptionArea()}
        {createTimeArea()}
      </div>
    )
  }

  const createBreakCard = (): React.ReactElement => {
    return (
      <div className="inner-card" style={{ background: (presenter.slotType === 'Break' || (!isCompleted && presenter.slotType === 'Presenter'))? getSpecialCardColor() : getNameAreaColor(), opacity: (isCompleted && isNarrowScreen && !retainCompletedSlotTimingVisuals())? .8: 1}}>
        {presenter.slotType === 'Break' && <div id='stripePattern'></div>}
        {getDeleteIcon()}
        {createCardName('breakChange')}
        {createTimeArea()}
      </div>
    )
  }

  const createHeaderCard = (): React.ReactElement => {
    return (
      <div className="inner-card inner-card-header">
        <div className='outter-name'>
          {getDeleteIcon()}
          {createCardName("headerChange")}
        </div>
      </div>
    )
  }

  const createNameArea = () => {
    return (
      <div className='name-area' style={{ width: "25%", backgroundColor: getNameAreaColor() }}>
        <div className='inner-name'>
          <DragHandle id="dragIcon" style={{ fontSize: `${dynamicFontSize}px`, visibility: shouldShowDragableIcon() ? "visible" : "hidden" }}></DragHandle>
          {createCardName("nameChange")}
          {createPriorityArea()}
          {getDeleteIcon()}
        </div>
      </div>
    );
  }

  const createDescriptionArea = () => {
    return (
      <div className='description-area' style={{ backgroundColor: getDescriptionColor()}}>
        <TextareaAutosize ref={descriptionRef} value={descriptionValue} id="descriptionInput" spellCheck={false}
          onChange={(e) => { setDescriptionValue(e.target.value); setDescriptionEdited(true) }}
          disabled={!canEditCardDescription()}
          onMouseOver={() => { setDescriptionFocus(canEditCardDescription()) }}
          onMouseOut={() => { setDescriptionFocus(false) }}
          onKeyDown={handleDescriptionKeydown}
          onBlur={() => { setDescriptionFocus(false); handleDescriptionChange() }}
          style={{
            fontSize: `${dynamicFontSize}px`,
            lineHeight: `${dynamicFontSize * 1.38}px`,
            textDecoration: canEditCardDescription() && descriptionFocus ? "underline dotted rgb(153, 0, 255, 1) 3px" : "underline dotted rgb(153, 0, 255, 0) 3px",
            WebkitMaskImage: getDescriptionShadow()
          }}
        />
      </div>
    )
  }

  const createTimeArea = () => {
    return (
      <div className={(isShortScreen)? "time-area-short": "time-area"}>
        {isShortScreen && (presenter.elapsedTime > 0 || isActive || isCompleted ) && createElapsedTime(false)}
        {/* {isShortScreen &&  <div style={{fontStyle:'normal', fontSize:`${dynamicFontSize*4/6}`}}> {getDisplayTime(presenter.elapsedTime, isCompleted)}&nbsp;</div>} */}
        {createInitialAndCurrentTime3(!isShortScreen)}
        {!isShortScreen  && (presenter.elapsedTime > 0 || isActive || isCompleted) && createElapsedTime(true)}
      </div>
    )
  }

  // =====================================================================================


  const createElapsedTime = (showUnitWhenComplete:boolean) => {
    return (
      <div 
        className={(isShortScreen)? 'short-elapsed-time': 'elapsed-time'}
        style={{ 
          fontSize: (isShortScreen)?`${dynamicFontSize*9/12}px`:`${dynamicFontSize*4/6}px`, 
        }}>
        { getDisplayTime(presenter.elapsedTime, showUnitWhenComplete && isCompleted && !showSlotDurationSeconds()) }
      </div>
    )
  }


  const setInitialDurationWithInputValidation = (event:React.ChangeEvent<HTMLInputElement>) => {
    let duration = event.target.value
    if ((/^\d+$/.test(duration) && parseInt(duration)*60 <= meetingLength  && parseInt(duration)*60 >= MIN_PRESENTER_TIME) || duration === "") {
      setInitialDurationValue(duration.toString());

      if (duration !== "") {
        let hypotheticalPresenters = JSON.parse(JSON.stringify(presenters));
        for (let i = 0; i < hypotheticalPresenters.length; i++) {
          if (hypotheticalPresenters[i].id === presenter.id) {
            hypotheticalPresenters[i] = {...presenter, initialDuration: parseInt(duration)*60};
            break;
          }
        }
        hypotheticalPresenters = adjustPresentersTime(cursor, meetingLength, [...hypotheticalPresenters]).presenters
        for (let i = 0; i < hypotheticalPresenters.length; i++) {
          if (hypotheticalPresenters[i].id === presenter.id) {
            setEstimatedCurrentDuration(getDisplayTime(hypotheticalPresenters[i].currentDuration))
          }
        }
      }
    }
  }

  const createInitialAndCurrentTime3 = (isDisplayShort: boolean) => {
    return (
      <div
        className='time-container'
        ref={timeRef}
        style={{fontSize:(isShortScreen)? `${dynamicFontSize}px`: `${dynamicFontSize*9/12}px`}}
        onMouseOver={() => setTimeHover(isPresenterChangeable())} 
        onMouseOut={() => setTimeHover(false)}
        
        
      >
        {(presenter.currentDuration < presenter.initialDuration ) && 
          <div className='initial-time' style={{borderBottom: createUnderline((anchorTimeChange !== null) )}}> {slotDurationLabels.initial} </div>
        }
        <div className={(anchorTimeChange)? 'time-editing-current': 'time-current'}
          onClick={ () => handleCurrentTimeClick() }
          style={{ borderBottom: createUnderline(timeHover && (anchorTimeChange === null) )}}
        >
          {slotDurationLabels.current}{isDisplayShort && !showSlotDurationSeconds() && <div>&nbsp;min</div>}
        </div>
        <Menu
          anchorEl={anchorTimeChange}
          // id="time-change-menu"
          open={openTimeChange}
          onClose={handleTimeChangeClose}
          PaperProps={{
            elevation: 0,

            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                left: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: 'left', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        >
          <div className='requested-time-title'>Edit Requested Time:</div>
          <div style={{display:'flex', justifyContent:'flex-end'}}>
          <TextField autoFocus size='small' variant="standard"
            // defaultValue={Math.floor(presenter.initialDuration/60)}
            value = {initialDurationValue} 
            onChange={setInitialDurationWithInputValidation} //KENDRA TODO add input checking
            style={{ fontSize:`${Math.max(dynamicFontSize*9/12, 12)}px`, width: '10ch'}}
            inputProps={{
              style: {  textAlign: 'right', fontStyle:'bold', fontFamily: 'ArtifaktElementBold', fontSize: `${Math.max(dynamicFontSize*9/12, 12)}px`},
            }}
            InputProps={{ 
              disableUnderline: true,
              endAdornment: 
              <InputAdornment position='end' style={{fontSize:`${Math.max(dynamicFontSize*9/12, 12)}px`, marginRight: '5px'}}><div >:00 min</div></InputAdornment>
            }}
          >
          </TextField>
          </div>
          <Divider></Divider>
          <div className='estimated-time-title'>Allotted Time: {estimatedCurrentDuration}</div>
        </Menu>
      </div>
    )
  }

  const createUnderline = (show:boolean) => {
    return show? "dotted rgb(153, 0, 255, 1) 3px" : "dotted rgb(153, 0, 255, 0) 3px";
  }


  const createCardName = (id: string) => {
    return (
      <TextareaAutosize id={id} value={nameValue} spellCheck={false} maxLength={20} maxRows={1}
        onMouseOver={() => { setNameEditFocus(true) }}
        onMouseOut={() => { setNameEditFocus(false) }}
        onBlur={() => { setNameEditFocus(false); handleNameChange() }}
        onKeyDown={handleNameKeydown}
        disabled={!canEditCardText()}
        onChange={(e) => { setNameValue(e.target.value); setNameEdited(true) }}
        style={{
          fontSize: `${dynamicFontSize}px`,
          textDecoration: createUnderline(canEditCardText() && nameEditFocus),
          transition: "text-decoration 300ms",
          textUnderlineOffset: "2.5px",
          lineHeight: `${dynamicFontSize * 1.38}px`,
          width: "100%",
          // overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
      ></TextareaAutosize>
    );
  }

  const getDeleteIcon = () => {

    return (
      <Delete
        onClick={() => { writeMeetingChange({ presenterDelete: [presenter] }) }}
        style={{
          fontSize: `${dynamicFontSize}px`,
          color: DELETE_ICON_COLOR,
          visibility: ((index === slotHover) && isPresenterChangeable()) ? "visible" : "hidden",
          zIndex: 2
        }}></Delete>
    )
  }


  const getDescriptionShadow = () => {
    const shadowSize = getDescriptionShadowSize();
    if (descriptionOverflow === "bottomGradient") return `linear-gradient(to bottom, black calc(100% - ${shadowSize}px), transparent`
    if (descriptionOverflow === "topGradient") return `linear-gradient(to top, black calc(100% - ${shadowSize}px), transparent 100%`
    if (descriptionOverflow === "bothGradient") return `linear-gradient( transparent, black ${shadowSize}px, black calc(100% - ${shadowSize}px), transparent) `
    return '';
  }

  const getDescriptionColor = () => {
    if (isCompleted && !retainCompletedSlotTimingVisuals()) return CARD_COMPLETED_COLOR;
    if (!isActive && !isCompleted) return CARD_INITIAL_COLOR;
    return '';
  }



  const createPriorityArea = () => {
    if (isPresenterChangeable() && priorityMenu) {
      return createPriorityMenu();
    }
    return (
      <div id="prioritySelect" style={{ width: `${dynamicFontSize}px`, fontSize: `${dynamicFontSize}px` }}>
        <Tooltip title={<Typography>Priority Select</Typography>} placement="right" arrow>
          {createPriorityIcon()}
        </Tooltip>
      </div>
    )
  }

  const createPriorityIcon = () => {
    if (priorityValue === 2) return (<PriorityHigh style={{ color: PRIORITY_ICON_COLOURS[priorityValue], fontSize: `${dynamicFontSize}px` }} onClick={() => { openPriorityMenu() }} />)
    if (priorityValue === 1) return (<MoreHoriz style={{ color: PRIORITY_ICON_COLOURS[priorityValue], fontSize: `${dynamicFontSize}px`, visibility: slotHover === index ? "visible" : "hidden", }} onClick={() => { openPriorityMenu() }} />)
    return (<ArrowDownward style={{ color: PRIORITY_ICON_COLOURS[priorityValue], fontSize: `${dynamicFontSize}px` }} onClick={() => { openPriorityMenu() }} />)
  }

  const createPriorityMenu = () => {
    return (
      <div id="prioritySelect" onMouseLeave={() => { setPriorityMenu(false) }}
        style={{ height: dynamicFontSize * 3, width: dynamicFontSize, fontSize: `${dynamicFontSize}px`, top: -dynamicFontSize }} >
        <Tooltip title={<Typography>{PRIORITY_STRINGS[2]}</Typography>} placement="right" arrow>
          <PriorityHigh style={{ color: PRIORITY_ICON_COLOURS[2], fontSize: `${dynamicFontSize}px` }} onClick={() => { handlePriorityChange(2) }} />
        </Tooltip>
        <Tooltip title={<Typography>{PRIORITY_STRINGS[1]}</Typography>} placement="right" arrow>
          <MoreHoriz style={{ color: PRIORITY_ICON_COLOURS[1], fontSize: `${dynamicFontSize}px` }} onClick={() => { handlePriorityChange(1) }} />
        </Tooltip>
        <Tooltip title={<Typography>{PRIORITY_STRINGS[0]}</Typography>} placement="right" arrow>
          <ArrowDownward style={{ color: PRIORITY_ICON_COLOURS[0], fontSize: `${dynamicFontSize}px` }} onClick={() => { handlePriorityChange(0) }} />
        </Tooltip>
      </div>
    )
  }



  return (
    <Draggable draggableId={`${presenter.id}`} index={index} isDragDisabled={!isPresenterChangeable()}>
      {provided => {
        const style = {
          height: presentationCardHeight, ...provided.draggableProps.style,
          color: isCompleted && !retainCompletedSlotTimingVisuals() ? 'grey' : '',
        };
        return (
          <div className="outter-card" ref={provided.innerRef} onMouseOver={() => { setSlotHover(index) }} onMouseOut={() => { setSlotHover(-2) }}
            {...provided.draggableProps}
            {...provided.dragHandleProps} style={style}>
            {shouldShowMeetingStartTime() &&
              <div id="startTime">
                <Typography id="indicatorText">
                  {getPresentationStartTime()}
                </Typography>
              </div>
            }
            {shouldShowPrenterTimes() &&
              <Fade in={index === slotHover}>
                <Typography id="onHoverStart">
                  {getPresentationStartTime()}
                </Typography>
              </Fade>
            }
            {shouldShowPrenterTimes() &&
              <Fade in={index === slotHover}>
                <Typography id="onHoverEnd">
                  {getPresentationEndTime()}
                </Typography>
              </Fade>

            }
            <Zoom in={index === slotHover}><div id="onHoverIndicator" style={{ visibility: shouldShowPrenterTimes() ? "visible" : "hidden" }}></div></Zoom>
            <ProgressIndicator active={isActive} position={getProgressIndicatorPosition()} />
            {createCard()}
            {shouldShowMeetingEndTime() &&
              <div id="estimatedEnd">
                <Typography id="indicatorText">
                  {getBottomEndTimeLabel()}
                </Typography>
              </div>
            }
            {shouldShowOverTime() &&
              <div id="endTime" style={{ bottom: getEndIndicatorHeight() }}>
                <Typography id="indicatorText">
                  {getScheduledEndTimeLabel()}
                </Typography>
              </div>
            }
            {descriptionEdited &&
              <Fade in={descriptionEdited}>
                <div id="submitInformation">
                  <Typography>
                    Click outisde the slot, or press Enter to confirm your changes. Press Esc to cancel the changes you have made
                  </Typography>
                </div>
              </Fade>
            }
          </div>
        )
      }}

    </Draggable>
  )
}
export default PresentationCard;